import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { Formik, Form } from 'formik';

import { SR_API_ACTION_AGENT_SIGNUP } from 'api/actionTypes';
import { USER_ROLE_ANONYMOUS } from 'common/userRoles';
import { SR_PARTNER_SIGNUP_USER_IS_ALREADY_PARTNER_ERROR } from 'common/errorCodes';
import {
  partnerAgentSignupTypeOptionItems,
  PARTNER_AGENT_SIGNUP_TYPE_LISTING_AGENT,
  PARTNER_AGENT_SIGNUP_TYPE_OTHER,
  PARTNER_AGENT_SIGNUP_TYPE_SUNRISE,
  PARTNER_AGENT_SIGNUP_TYPE_TENANT_SCREENING,
} from 'common/partnerConstants';

import { getUserLandingPage } from 'client/helpers/allowedRoles';
import {
  createFormValidationFn,
  createInitialValues,
  hideFieldIf,
} from 'client/helpers/formikHelpers';
import withTransitionHook from 'client/helpers/withTransitionHook';

import branch from 'client/core/branch';
import withApiState from 'client/helpers/withApiState';
import AuthActions from 'client/actions/Auth';
import SalesRepActions from 'client/actions/SalesRep';

import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrCard from 'client/components/SrCard/SrCard';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrLink from 'client/components/SrLink/SrLink';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrFooterSaveButton from 'client/components/SrFooterSaveButton';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

const getShouldAskSunroomReference = (agentSignupReasonType) => {
  if (typeof agentSignupReasonType !== 'string') {
    return false;
  }

  const validAgentSignupReasons = [
    PARTNER_AGENT_SIGNUP_TYPE_TENANT_SCREENING,
    PARTNER_AGENT_SIGNUP_TYPE_SUNRISE,
    PARTNER_AGENT_SIGNUP_TYPE_LISTING_AGENT,
  ];

  return validAgentSignupReasons.includes(agentSignupReasonType);
};

const getFormFields = ({ salesReps = [], formValues }) => {
  const shouldAskSunroomReference = getShouldAskSunroomReference(formValues.agentSignupReasonType);
  const formFields = [
    {
      id: 'name',
      title: 'Full Name',
      type: 'short_text',
      validations: ['required'],
    },
    {
      id: 'email',
      title: 'Email',
      type: 'email',
      validations: ['required', 'email'],
    },
    {
      id: 'phone',
      title: 'Phone',
      type: 'phone',
      validations: ['required', 'phone'],
    },
    {
      id: 'password',
      title: 'New Password',
      type: 'password',
      validations: ['required'],
    },
    {
      id: 'confirmPassword',
      title: 'Confirm Password',
      type: 'password',
      validations: ['required', 'confirmPassword:password'],
    },
    {
      id: 'managementCompanyName',
      title: 'What is the name of your property management company?',
      type: 'short_text',
      validations: ['required'],
    },
    {
      id: 'totalManagedDoors',
      title: 'How many doors do you have under management?',
      type: 'short_text',
      validations: ['required', 'nonNegativeNumber'],
      properties: {
        onlyAcceptNumbers: true,
        pattern: '[0-9]+',
      },
    },
    {
      id: 'agentSignupReasonType',
      title: 'Why are you interested in using Sunroom?',
      type: 'select',
      properties: {
        choices: partnerAgentSignupTypeOptionItems.map((option) => ({
          value: option.value,
          label: option.label,
        })),
      },
      validations: ['required'],
    },
    hideFieldIf(formValues.agentSignupReasonType !== PARTNER_AGENT_SIGNUP_TYPE_OTHER, {
      id: 'otherAgentSignupReasonProvided',
      title: 'Other reason:',
      type: 'short_text',
      validations: ['required'],
    }),
    hideFieldIf(salesReps.length < 1
      || !shouldAskSunroomReference, {
      id: 'salesRepUserId',
      title: 'Who at Sunroom have you spoken to?',
      type: 'select',
      properties: {
        enableResetOption: true,
        placeholder: 'I haven’t spoken with anyone at Sunroom',
        choices: salesReps.map((salesRep) => ({
          value: salesRep.id,
          label: salesRep.name,
        })),
      },
    }),
  ];
  return formFields;
};

@withTransitionHook
@withApiState({
  agentSignupApiState: {
    action: SR_API_ACTION_AGENT_SIGNUP,
  },
})
@branch({
  currentUser: ['currentUser'],
  isPartnerApp: ['mobileApp', 'isPartnerApp'],
  salesReps: ['salesReps'],
})
class AgentSignUpPage extends React.Component {
  static getPageMetadata = () => ({
    pageTitle: 'Get Started | Sunroom Rentals',
    section: 'Onboarding',
  })

  static propTypes = {
    agentSignupApiState: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    isPartnerApp: PropTypes.bool.isRequired,
    salesReps: PropTypes.array.isRequired,
  }

  static fetchData(state, inject) {
    return Promise.all([
      SalesRepActions.getSalesRepList(inject),
    ]);
  }

  _onSubmit = (formValues) => {
    const { dispatch } = this.props;
    dispatch(AuthActions.agentSignup, {
      ...formValues,
    });
  }

  render() {
    const {
      currentUser,
      location,
      agentSignupApiState: { error, inProgress },
      isPartnerApp,
      salesReps,
    } = this.props;

    if (!currentUser.roles.includes(USER_ROLE_ANONYMOUS)) {
      return (
        <Redirect to={getUserLandingPage({
          userRoles: currentUser.roles,
        })}
        />
      );
    }

    const formFields = getFormFields({ salesReps, formValues: {} });
    const errorMessage = error && (
      (error.constraint === 'users_phone_unique'
        && 'There is already an account with that phone number.')
      || (error.constraint === 'users_email_unique'
        && 'There is already an account with that email.')
      || (error.constraint === 'partners_slug_unique'
        && 'There is already an agent with that name.')
      || error.message
    );

    return (
      <SrPartnerPageV2
        backUrl={isPartnerApp ? '/login' : '/get-started'}
        navbarImage='/images/SRlogo.svg'
        pageHeader='Signup to Simplify Your Leasing'
        pageSubtitle='When you partner with Sunroom, you get an entire leasing
        team and a technology platform. We help you keep track of your properties
        in real-time, find quality applicants, and get a lease signed 26% faster than the market average.'
      >
        <SrCard>
          <Formik
            ref={(formik) => {
              this.formik = formik;
            }}
            initialValues={createInitialValues({ formFields })}
            validate={(formValues) => {
              return createFormValidationFn(
                getFormFields({ salesReps, formValues })
              )(formValues);
            }}
            onSubmit={this._onSubmit}
            render={(formikBag) => (
              <Form>
                {getFormFields({
                  salesReps,
                  formValues: formikBag.values,
                }).map((formField, index) =>
                  <SrFormikField
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                    key={formField.id}
                    margin={index === 0 ? '0' : null}
                  />
                )}
                <SrStaticFooter>
                  <SrText
                    display='block'
                    lineHeight='25px'
                    font='greycliff'
                    color='grey400'
                    fontSize='13px'
                    weight='500'
                  >
                    1 min to complete
                  </SrText>
                  <SrFooterSaveButton
                    isSubmitting={inProgress}
                    buttonText='Sign up'
                  />
                </SrStaticFooter>
              </Form>
            )}
          />
        </SrCard>

        <SrBox
          marginTop='26px'
          textAlign='center'
        >
          <SrText
            font='greycliff'
            weight='bold'
          >
            Already have an account?
            &nbsp;&nbsp;
            <SrLink to={`/login${location.search}`} color='blue300'>
              Log In &gt;
            </SrLink>
          </SrText>
        </SrBox>

        {errorMessage && error?.name !== SR_PARTNER_SIGNUP_USER_IS_ALREADY_PARTNER_ERROR && (
          <SrBox
            marginTop='10px'
            borderRadius='3px'
            overflow='hidden'
          >
            <SrErrorMessage>
              {errorMessage}
            </SrErrorMessage>
          </SrBox>
        )}
        {error?.name === SR_PARTNER_SIGNUP_USER_IS_ALREADY_PARTNER_ERROR && (
          <SrBox
            marginTop='10px'
            borderRadius='3px'
            overflow='hidden'
          >
            <SrErrorMessage>
              {'It looks like you already have an account, please login '}
              <SrLink to={`/login${location.search}`}>
                <SrText
                  weight='600'
                  color='white'
                  decoration='underline'
                  size='14'
                >
                  here
                </SrText>
              </SrLink>
              .
            </SrErrorMessage>
          </SrBox>
        )}
      </SrPartnerPageV2>
    );
  }
}

AgentSignUpPage.displayName = 'AgentSignUpPage';

export default AgentSignUpPage;
